import { useState,useContext } from 'react';
import { useNavigate } from "react-router-dom";
import axios from "axios";
import config from '../config.js';
import PropTypes from 'prop-types';
import ListItemText from '@mui/material/ListItemText';
import ListItemIcon from '@mui/material/ListItemIcon';
import MenuItem from '@mui/material/MenuItem';
import MenuList from '@mui/material/MenuList';
import Popover from '@mui/material/Popover';
import DownloadIcon from '@mui/icons-material/Download';
import VisibilityIcon from '@mui/icons-material/Visibility';
import Backdrop from '@mui/material/Backdrop';
import QueryStatsIcon from '@mui/icons-material/QueryStats';

import Grid from '@mui/material/Grid';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';

import { AuthContext } from "../context/AuthContext";
import { CloudContext } from "../context/CloudContext";
import {getPresignedURLS3,getPresignedURLForViewer,isViewerReady} from "../utils/interfaceS3";
import { useTranslation } from "react-i18next";
import { useSnackbar } from 'notistack';

import FilePresentIcon from '@mui/icons-material/FilePresent';
import ProductsDialogAdmin from "../dialog/ProductsDialogAdmin.jsx"
import RequestDialog from "../dialog/RequestDialog.jsx"

export const FilePopoverAdmin = (props) => {

  const {  t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const navigate = useNavigate();

  const { jwt, idToken, service } = useContext(AuthContext);
  const { region, idpool, cesium_token } = useContext(CloudContext);

  const { anchorEl, onClose, open, data, userid, processings, reloadData } = props;

  // Check if file is an archive (zip)
  const isArchive = data?.filename?.toLowerCase().endsWith('.zip');

  const [isViewerLoading, setIsViewerLoading] = useState(false);
  const [openDialogDeleteFile, setOpenDialogDeleteFile] = useState(false);
  const [openDialogProducts, setOpenDialogProducts] = useState(false);
  const [openDialogRequest, setOpenDialogRequest] = useState(false);

  async function handleDownloadFileClick () {
    try{
      const url = await getPresignedURLS3(userid,idToken, region, idpool,`${data.filename}`,3600);
      window.location.href = url;
    }catch{
      enqueueSnackbar(`${t("errorDownloadFile")}`, {variant:'error'} );
    }
  };

  const handleProductsClick = num => {
    setOpenDialogProducts(true);
    onClose();
  };

  const handleRequestClick = num => {
    setOpenDialogRequest(true);
    onClose();
  };

  async function getProducts(id) {
    try {
      const response = await axios.get(`${config.apiUrl}/api/datas/${id}?populate=*`, {
        headers: {
          Authorization: `Bearer ${jwt}`,
        }
      });
      //console.log('getProducts response:', response);
      
      const resultsData = response.data.data.attributes.results?.data;
      return resultsData;

    } catch (error) {
      console.error('Error fetching products:', error);
      return null;
    }
  }

  async function handleView () {
    if(service==="classic"){
      setIsViewerLoading(true);
      let viewerReady = await isViewerReady(userid,idToken, region, idpool,`${data.filename}`);
      if(viewerReady){
        const {metadata,hierarchy,octree} = await getPresignedURLForViewer(userid,idToken, region, idpool,`${data.filename}`,86400);
        const filename=data.filename;
        const description=data.description;
        setIsViewerLoading(false);
        navigate("/viewer-page/", { state: { filename:filename, description:description, metadata:metadata, hierarchy:hierarchy, octree:octree } });     
      }else{
        setIsViewerLoading(false);
        enqueueSnackbar(`${t("ViewerNotReady")}`, {variant:'info'} );
      }
      onClose();
    }
    else if (service === "mapping") {
      //console.log('Mapping data:', data);
      
      setIsViewerLoading(true);
      const products = await getProducts(data.id);
            const resultsIdCesium = products
        .map(result => result.attributes.id_cesium)
        .filter(id => id != null); // Filter out null/undefined values
      //console.log('Final resultsIdCesium:', resultsIdCesium);
      
      setIsViewerLoading(false);

        navigate("/cesium-viewer-page/", { 
          state: { 
            mappingAssetId: data.id_cesium, 
            gaussianSplattingId: data.id_cesium_gs,     
            resultsAssetId: resultsIdCesium,
            cesium_token: cesium_token
          } 
        }); 
        onClose();
    }
  };

  return (
    <>
      <Backdrop
      sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1000 }}
      open={isViewerLoading}
      >     
        <Grid
        container
        sx={{ alignItems: 'center', justifyContent: 'center', alignContent: 'center' }}
        >
        <Grid size={12} sx={{ py: 3, textAlign: 'center' }}>
        <CircularProgress color="inherit" size="5rem"/>
        </Grid>
        <Grid size={12} sx={{ textAlign: 'center' }}>
            <Typography variant="h5" color="inherit">
            <b>{t("GettingViewerReady")}</b>
            </Typography>
        </Grid>
        </Grid>
      </Backdrop>
      <Popover
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        onClose={onClose}
        open={open}
        slotProps={{ paper: { sx: { width: 160 } } }}
      >
        <MenuList
          disablePadding
          dense
          sx={{
            p: '8px',
            '& > *': {
              borderRadius: 1
            }
          }}
        >
          {isArchive ? [
              <MenuItem key="download" onClick={(event) => handleDownloadFileClick(event)}>
                <ListItemIcon>
                  <DownloadIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>{t("Download")}</ListItemText>
              </MenuItem>
          ] : [
              <MenuItem key="view" onClick={(event) => handleView(event)}>
                <ListItemIcon>
                  <VisibilityIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>{t("View")}</ListItemText>
              </MenuItem>,
              data.processing_requests.length>0 && <MenuItem key="requests" onClick={(event) => handleRequestClick(event)}>
                <ListItemIcon>
                  <QueryStatsIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>{t("Requests")}</ListItemText>
              </MenuItem>,
              <MenuItem key="products" onClick={(event) => handleProductsClick(event)}>
                <ListItemIcon>
                  <FilePresentIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>{t("Products")}</ListItemText>
              </MenuItem>,
              <MenuItem key="download" onClick={(event) => handleDownloadFileClick(event)}>
                <ListItemIcon>
                  <DownloadIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>{t("Download")}</ListItemText>
              </MenuItem>
          ]}
        </MenuList>
      </Popover>
      <ProductsDialogAdmin userid={userid} id={data.id} open={openDialogProducts} setOpen={setOpenDialogProducts} />
      {data.processing_requests.length>0 && <RequestDialog processing_requests={data.processing_requests} processings={processings} open={openDialogRequest} setOpen={setOpenDialogRequest} />}
     </>
  );
};

FilePopoverAdmin.propTypes = {
  anchorEl: PropTypes.any,
  onClose: PropTypes.func,
  open: PropTypes.bool.isRequired
};
